// ABOUTME: /1 — viewer for the per-machine event logs generated from server/data.xlsx.
// ABOUTME: Fetches /machine-logs.json (built by scripts/build-machine-logs.ts) and renders timelines.

import { supabase } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { jwtDecode } from "jwt-decode";
import { useEffect, useMemo, useState } from "react";

interface CleanEvent {
  start: string;
  end: string;
  reason: string;
  durationSecs: number;
}

interface MachineLog {
  machine: string;
  area: string;
  startedAt: string;
  endedAt: string;
  totalSecs: number;
  runningSecs: number;
  idleSecs: number;
  downtimeSecs: number;
  eventCount: number;
  reasonTotals: Record<string, number>;
  events: CleanEvent[];
}

interface LogPayload {
  generatedAt: string;
  source: string;
  machines: MachineLog[];
}

interface JWTPayload {
  user_role?: string;
}

const REASON_COLORS: Record<string, string> = {
  Running: "#22c55e",
  Idle: "#94a3b8",
};
const DOWNTIME_PALETTE = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#a855f7",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#f43f5e",
  "#14b8a6",
  "#8b5cf6",
];

function colorFor(reason: string, downtimeReasons: string[]): string {
  if (REASON_COLORS[reason]) return REASON_COLORS[reason];
  const idx = downtimeReasons.indexOf(reason);
  if (idx === -1) return "#64748b";
  return DOWNTIME_PALETTE[idx % DOWNTIME_PALETTE.length];
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function pct(part: number, total: number): string {
  if (total <= 0) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
}

export const Route = createFileRoute("/_authenticated/machine-logs")({
  component: MachineLogsPage,
});

function MachineLogsPage() {
  const { data: isSuperadmin = false, isLoading: isCheckingRole } = useQuery({
    queryKey: ["sfc-mockup-superadmin-check"],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) return false;
      const decoded = jwtDecode<JWTPayload>(session.access_token);
      return decoded.user_role === "superadmin";
    },
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (isCheckingRole) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (!isSuperadmin) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-sm text-muted-foreground">
        You do not have permission to view this page.
      </div>
    );
  }

  return <MachineLogsView />;
}

function MachineLogsView() {
  const [data, setData] = useState<LogPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    fetch("/machine-logs.json")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<LogPayload>;
      })
      .then((payload) => {
        setData(payload);
        setSelected(payload.machines[0]?.machine ?? null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) {
    return <div className="p-8 text-red-600">Failed to load logs: {error}</div>;
  }
  if (!data) {
    return <div className="p-8 text-slate-500">Loading…</div>;
  }

  const machine = data.machines.find((m) => m.machine === selected);

  return (
    <div className="min-h-dvh bg-slate-50 p-6 font-sans">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          Machine event logs
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Source: <code>{data.source}</code> · Generated{" "}
          {new Date(data.generatedAt).toLocaleString()} · {data.machines.length}{" "}
          machines
        </p>
      </header>

      <nav className="mb-6 flex flex-wrap gap-2">
        {data.machines.map((m) => {
          const active = m.machine === selected;
          return (
            <button
              type="button"
              key={m.machine}
              onClick={() => setSelected(m.machine)}
              className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              <span className="font-medium">{m.machine}</span>
              <span
                className={`ml-2 text-xs ${active ? "text-slate-300" : "text-slate-400"}`}
              >
                {m.area}
              </span>
            </button>
          );
        })}
      </nav>

      {machine ? <MachineDetail machine={machine} /> : null}
    </div>
  );
}

function MachineDetail({ machine }: { machine: MachineLog }) {
  const downtimeReasons = useMemo(() => {
    return Object.keys(machine.reasonTotals)
      .filter((r) => r !== "Running" && r !== "Idle")
      .sort(
        (a, b) =>
          (machine.reasonTotals[b] ?? 0) - (machine.reasonTotals[a] ?? 0),
      );
  }, [machine]);

  const startMs = new Date(machine.startedAt).getTime();
  const endMs = new Date(machine.endedAt).getTime();
  const spanMs = Math.max(endMs - startMs, 1);

  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Running"
          value={pct(machine.runningSecs, machine.totalSecs)}
          sub={formatDuration(machine.runningSecs)}
          accent="#22c55e"
        />
        <Stat
          label="Idle"
          value={pct(machine.idleSecs, machine.totalSecs)}
          sub={formatDuration(machine.idleSecs)}
          accent="#94a3b8"
        />
        <Stat
          label="Downtime"
          value={pct(machine.downtimeSecs, machine.totalSecs)}
          sub={formatDuration(machine.downtimeSecs)}
          accent="#ef4444"
        />
        <Stat
          label="Events"
          value={String(machine.eventCount)}
          sub={`${formatTime(machine.startedAt)} → ${formatTime(machine.endedAt)}`}
          accent="#0f172a"
        />
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-medium text-slate-700">Timeline</h2>
        <div className="relative h-10 w-full overflow-hidden rounded-md bg-slate-100">
          {machine.events.map((e, i) => {
            const s = new Date(e.start).getTime();
            const left = ((s - startMs) / spanMs) * 100;
            const width = ((e.durationSecs * 1000) / spanMs) * 100;
            return (
              <div
                key={`${e.start}-${i}`}
                title={`${e.reason} · ${formatDuration(e.durationSecs)} · ${formatTime(e.start)}`}
                className="absolute top-0 h-full"
                style={{
                  left: `${left}%`,
                  width: `${Math.max(width, 0.05)}%`,
                  background: colorFor(e.reason, downtimeReasons),
                }}
              />
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-600">
          <Legend
            color="#22c55e"
            label={`Running ${formatDuration(machine.runningSecs)}`}
          />
          <Legend
            color="#94a3b8"
            label={`Idle ${formatDuration(machine.idleSecs)}`}
          />
          {downtimeReasons.map((r) => (
            <Legend
              key={r}
              color={colorFor(r, downtimeReasons)}
              label={`${r} ${formatDuration(machine.reasonTotals[r] ?? 0)}`}
            />
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white">
        <h2 className="border-b border-slate-200 px-4 py-3 text-sm font-medium text-slate-700">
          Events ({machine.events.length})
        </h2>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Start</th>
                <th className="px-4 py-2 font-medium">End</th>
                <th className="px-4 py-2 font-medium">Reason</th>
                <th className="px-4 py-2 text-right font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {machine.events.map((e, i) => (
                <tr
                  key={`${e.start}-${i}`}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">
                    {formatTime(e.start)}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">
                    {formatTime(e.end)}
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{
                          background: colorFor(e.reason, downtimeReasons),
                        }}
                      />
                      {e.reason}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">
                    {formatDuration(e.durationSecs)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: accent }}
        />
        <span className="text-xs uppercase tracking-wide text-slate-500">
          {label}
        </span>
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      <div className="mt-0.5 text-xs text-slate-500">{sub}</div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-2.5 w-2.5 rounded-sm"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}
